import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import JsBarcode from 'jsbarcode';
import { FaBarcode, FaBox, FaSearch, FaDownload } from 'react-icons/fa';
import AssistantSidebar from './AssistantSidebar';
import { API_BASE_URL } from '../config/api';
import '../styles/GenerateBarcode.css';

const GenerateBarcode = () => {
  const navigate = useNavigate();
  const user = JSON.parse(sessionStorage.getItem('user') || '{}');
  const barcodeRef = useRef(null);

  const [assets, setAssets] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedAsset, setSelectedAsset] = useState(null);
  const [selectedUnitId, setSelectedUnitId] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [barcodeData, setBarcodeData] = useState(null);
  const [generatingBarcode, setGeneratingBarcode] = useState(false);
  const [generatedBarcodes, setGeneratedBarcodes] = useState([]);
  const [showGeneratedList, setShowGeneratedList] = useState(false);
  const [imageLoadError, setImageLoadError] = useState(false);

  const fetchAssets = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const token = sessionStorage.getItem('token');
      const response = await axios.get(`${API_BASE_URL}/api/assets?viewAll=true`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.success) {
        setAssets(response.data.data || []);
      }
    } catch (err) {
      setError('Failed to load assets');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchGeneratedBarcodes = useCallback(async () => {
    try {
      const token = sessionStorage.getItem('token');
      const response = await axios.get(`${API_BASE_URL}/api/asset-users/barcodes`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data?.success) {
        setGeneratedBarcodes(response.data.data || []);
      }
    } catch (err) {
      console.error('Error fetching generated barcodes:', err);
    }
  }, []);

  useEffect(() => {
    if (!['asset_incharge', 'admin'].includes(user.role)) {
      navigate('/');
      return;
    }
    fetchAssets();
    fetchGeneratedBarcodes();
  }, [user.role, navigate, fetchAssets, fetchGeneratedBarcodes]);

  useEffect(() => {
    if (barcodeData && barcodeRef.current) {
      try {
        JsBarcode(barcodeRef.current, barcodeData.barcodeValue, {
          format: 'CODE128',
          width: 3,
          height: 100,
          displayValue: true,
          fontSize: 16,
          margin: 10
        });
         setImageLoadError(false);
      } catch (err) {
        
        setImageLoadError(true);
      }
    }
  }, [barcodeData]);

  const filteredAssets = assets.filter((asset) =>
    asset.assetName.toLowerCase().includes(searchTerm.toLowerCase()) ||
    asset.serialNumber?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleSelectAsset = (asset) => {
    setSelectedAsset(asset);
    setSelectedUnitId('');
    setBarcodeData(null);
    setShowModal(true);
  };

  const toUrlSafeBase64 = (value) => {
    return btoa(value).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/g, '');
  };

  const handleGenerateBarcode = async () => {
    if (!selectedUnitId) {
      setError('Please select a Unit ID');
      return;
    }

    setGeneratingBarcode(true);
    setError('');
    setImageLoadError(false);
    try {
      const barcodeValue = `${selectedAsset._id}_${selectedUnitId}`;

      const token = sessionStorage.getItem('token');
  
      const generateResponse = await axios.post(
        `${API_BASE_URL}/api/asset-users/barcodes/generate`,
        { assetId: selectedAsset._id, unitId: selectedUnitId },
        { headers: { Authorization: `Bearer ${token}` } }
      );

   

      if (!generateResponse.data.success) {
        setError(generateResponse.data.message || 'Failed to generate barcode');
        return;
      }

      const encodedValue = generateResponse.data?.data?.encodedPayload || toUrlSafeBase64(barcodeValue);
      const scanUrl = `${window.location.origin}/barcode/${encodedValue}`;

    

      setBarcodeData({
        assetId: selectedAsset._id,
        assetName: selectedAsset.assetName,
        unitId: selectedUnitId,
        serialNumber: selectedAsset.serialNumber,
        barcodeValue: barcodeValue,
        encodedValue: encodedValue,
        scanUrl
      });
      fetchGeneratedBarcodes();
    } catch (err) {
      const errorMsg = err.response?.data?.message || err.message || 'Failed to generate barcode';
      setError(errorMsg);
      console.error('Barcode generation error:', err);
    } finally {
      setGeneratingBarcode(false);
    }
  };

  const handleDownloadBarcode = async () => {
    if (!barcodeData || !barcodeRef.current) return;

    try {
      // Get SVG as data URL
      const svgElement = barcodeRef.current;
      const serializer = new XMLSerializer();
      const svgString = serializer.serializeToString(svgElement);
      const svgBlob = new Blob([svgString], { type: 'image/svg+xml' });
      const svgUrl = URL.createObjectURL(svgBlob);

      // Create a temporary image to convert SVG to PNG
      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement('canvas');
        canvas.width = img.width;
        canvas.height = img.height;
        const ctx = canvas.getContext('2d');
        ctx.fillStyle = 'white';
        ctx.fillRect(0, 0, canvas.width, canvas.height);
        ctx.drawImage(img, 0, 0);

        // Download as PNG
        canvas.toBlob((blob) => {
          const pngUrl = URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = pngUrl;
          link.download = `barcode_${barcodeData.unitId}.png`;
          document.body.appendChild(link);
          link.click();
          link.remove();
          URL.revokeObjectURL(pngUrl);
          URL.revokeObjectURL(svgUrl);
        });
      };
      img.onerror = () => {
        setError('Unable to convert barcode to image. Please retry.');
        URL.revokeObjectURL(svgUrl);
      };
      img.src = svgUrl;
    } catch (downloadErr) {
      setError('Unable to download barcode. Please retry.');
      console.error(downloadErr);
    }
  };

  const handleDownloadFromList = async (barcodeRow) => {
    try {
      const barcodeValue = `${barcodeRow.assetId}_${barcodeRow.unitId}`;
      
      // Create a temporary SVG element
      const tempSvg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
      tempSvg.setAttribute('id', `temp-barcode-${barcodeRow.unitId}`);
      document.body.appendChild(tempSvg);
      
      // Generate barcode on the temporary SVG
      JsBarcode(tempSvg, barcodeValue, {
        format: 'CODE128',
        width: 3,
        height: 100,
        displayValue: true,
        fontSize: 16,
        margin: 10
      });

      // Convert SVG to image and download
      const serializer = new XMLSerializer();
      const svgString = serializer.serializeToString(tempSvg);
      const svgBlob = new Blob([svgString], { type: 'image/svg+xml' });
      const svgUrl = URL.createObjectURL(svgBlob);

      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement('canvas');
        canvas.width = img.width;
        canvas.height = img.height;
        const ctx = canvas.getContext('2d');
        ctx.fillStyle = 'white';
        ctx.fillRect(0, 0, canvas.width, canvas.height);
        ctx.drawImage(img, 0, 0);

        canvas.toBlob((blob) => {
          const pngUrl = URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = pngUrl;
          link.download = `barcode_${barcodeRow.unitId}.png`;
          document.body.appendChild(link);
          link.click();
          link.remove();
          URL.revokeObjectURL(pngUrl);
          URL.revokeObjectURL(svgUrl);
          document.body.removeChild(tempSvg);
        });
      };
      img.onerror = () => {
        setError('Unable to convert barcode to image. Please retry.');
        URL.revokeObjectURL(svgUrl);
        document.body.removeChild(tempSvg);
      };
      img.src = svgUrl;
    } catch (downloadErr) {
      setError('Unable to download barcode. Please retry.');
      console.error(downloadErr);
    }
  };

  const formatDate = (value) => {
    if (!value) return 'N/A';
    return new Date(value).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="generate-barcode-container">
      <AssistantSidebar user={user} activeTab="generate-barcode" />

      <div className="barcode-content">
        <div className="barcode-header">
          <h1><FaBarcode /> Generate Barcode</h1>
          <p>Select an asset and unit ID to generate a barcode</p>
          <button className="btn btn-primary generated-list-btn" onClick={() => setShowGeneratedList(true)}>
            View Generated Barcodes ({generatedBarcodes.length})
          </button>
        </div>

        {error && <div className="error-message">{error}</div>}

        <div className="barcode-main">
          <div className="assets-section">
            <div className="section-header">
              <h2>Available Assets</h2>
              <div className="search-box">
                <FaSearch className="search-icon" />
                <input
                  type="text"
                  placeholder="Search by asset name or serial number..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
            </div>

            {loading ? (
              <div className="loading">Loading assets...</div>
            ) : filteredAssets.length === 0 ? (
              <div className="no-assets">No assets found</div>
            ) : (
              <div className="assets-grid">
                {filteredAssets.map((asset) => (
                  <div
                    key={asset._id}
                    className="asset-card"
                    onClick={() => handleSelectAsset(asset)}
                  >
                    <div className="asset-card-header">
                      <FaBox className="asset-icon" />
                      <span className="quantity-badge">{asset.quantity}</span>
                    </div>
                    <div className="asset-card-body">
                      <h3>{asset.assetName}</h3>
                      <p className="serial-number">{asset.serialNumber}</p>
                      <p className="category">{asset.category}</p>
                      <p className="department">{asset.department}</p>
                    </div>
                    <div className="asset-card-footer">
                      <span className={`status-badge status-${asset.status}`}>
                        {asset.status.replace('_', ' ')}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {selectedAsset && showModal && (
            <div className="barcode-modal-overlay" onClick={() => setShowModal(false)}>
              <div className="barcode-modal" onClick={(e) => e.stopPropagation()}>
                <div className="modal-header">
                  <h2>Generate Barcode</h2>
                  <button
                    className="modal-close"
                    onClick={() => setShowModal(false)}
                  >
                    ✕
                  </button>
                </div>

                <div className="modal-body">
                  <div className="asset-info">
                    <h3>{selectedAsset.assetName}</h3>
                    <p><strong>Serial Number:</strong> {selectedAsset.serialNumber}</p>
                    <p><strong>Category:</strong> {selectedAsset.category}</p>
                    <p><strong>Total Units:</strong> {selectedAsset.quantity}</p>
                  </div>

                  <div className="unit-selection">
                    <label>Select Unit ID:</label>
                    <select
                      value={selectedUnitId}
                      onChange={(e) => setSelectedUnitId(e.target.value)}
                      className="unit-select"
                    >
                      <option value="">-- Choose Unit ID --</option>
                      {selectedAsset.units && selectedAsset.units.map((unit) => (
                        <option key={unit.unitId} value={unit.unitId}>
                          {unit.unitId} ({unit.status})
                        </option>
                      ))}
                    </select>
                  </div>

                  {barcodeData ? (
                    <div className="barcode-display">
                      <h3>Barcode Generated</h3>
                      <div className="barcode-info">
                        <p><strong>Unit ID:</strong> {barcodeData.unitId}</p>
                        <p><strong>Barcode Value:</strong> {barcodeData.barcodeValue}</p>
                        <p><strong>Scan URL:</strong> <a href={barcodeData.scanUrl} target="_blank" rel="noopener noreferrer">{barcodeData.scanUrl}</a></p>
                      </div>
                      <div className="barcode-preview">
                        {imageLoadError ? (
                          <div className="barcode-error">
                            Failed to generate barcode. Please try again.
                          </div>
                        ) : (
                          <svg 
                            ref={barcodeRef}
                            id="barcode-svg"
                          ></svg>
                        )}
                      </div>
                      <button
                        className="btn btn-primary download-btn"
                        onClick={handleDownloadBarcode}
                      >
                        <FaDownload /> Download Barcode
                      </button>
                    </div>
                  ) : (
                    <button
                      className="btn btn-primary generate-btn"
                      onClick={handleGenerateBarcode}
                      disabled={!selectedUnitId || generatingBarcode}
                    >
                      {generatingBarcode ? 'Generating...' : 'Generate Barcode'}
                    </button>
                  )}
                </div>
              </div>
            </div>
          )}

          {showGeneratedList && (
            <div className="barcode-modal-overlay" onClick={() => setShowGeneratedList(false)}>
              <div className="barcode-modal generated-list-modal" onClick={(e) => e.stopPropagation()}>
                <div className="modal-header">
                  <h2>Generated Barcodes</h2>
                  <button className="modal-close" onClick={() => setShowGeneratedList(false)}>
                    ✕
                  </button>
                </div>
                <div className="modal-body">
                  {generatedBarcodes.length === 0 ? (
                    <div className="no-assets">No generated barcodes yet</div>
                  ) : (
                    <div className="generated-table-wrap">
                      <table className="generated-table">
                        <thead>
                          <tr>
                            <th>#</th>
                            <th>Asset</th>
                            <th>Unit ID</th>
                            <th>Serial</th>
                            <th>Generated</th>
                            <th>Download</th>
                          </tr>
                        </thead>
                        <tbody>
                          {generatedBarcodes.map((row, idx) => {
                            return (
                              <tr key={row._id || `${row.assetId}-${row.unitId}`}>
                                <td>{idx + 1}</td>
                                <td>{row.assetName}</td>
                                <td>{row.unitId}</td>
                                <td>{row.serialNumber || 'N/A'}</td>
                                <td>{formatDate(row.updatedAt)}</td>
                                <td>
                                  <button 
                                    className="btn-download-small"
                                    onClick={() => handleDownloadFromList(row)}
                                  >
                                    <FaDownload /> Download
                                  </button>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default GenerateBarcode;
